import { useEffect, useMemo, useCallback } from 'react';
import { useLoadingContext } from '@features/contexts/LoadingProvider';
import { nanoid } from '@reduxjs/toolkit';

/**
 * @returns An object with a `setLoading` function and the `loading`
 * value for the allocated state. The state is automatically cleaned
 * up prior to unmount.
 */
export const useSetLoading = (initial = false) => {
    const { setLoading: setLoad, removeState, states } = useLoadingContext();
    const id = useMemo(() => nanoid(8), []);

    const setLoading = useCallback((isLoading: boolean) => setLoad(id, isLoading), []);

    useEffect(() => {
        setLoad(id, initial);
        return () => removeState(id);
    }, []);

    return {
        /**
         * Whether or not the allocated loading state is currently `true`.
         */
        loading: states[id] ?? false,
        /**
         * Set the allocated loading state.
         */
        setLoading,
    };
};
